import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@progress/kendo-react-buttons';
import { SVGIcon } from '@progress/kendo-svg-icons';
import React, { CSSProperties, useMemo, useState } from 'react';
import { Menu, MenuItem } from '../../../components/common/menu/DropdownMenu';
import { ContextMenus } from '../../../components/map-interaction/context-menu/ContextMenus';
import { RouteAnalyzer } from '../../../components/route-analyzer/RouteAnalyzer';
import {
    AddOutline,
    AirportListOutline,
    AirportTowerOutline,
    ChartsLineOutline,
    CloseOutline,
    DragOutline,
    FlightListOutline
} from '../../../icons/icons';
import AirportList from '../../features/airport-list/AirportList';
import { FlightList } from '../../features/flight-list/FlightList';
import { AirportMonitor } from '../../features/home/panels/airport/AirportMonitor';
import { EventList } from '../../features/home/panels/events/EventList';
import FleetMonitor from '../../features/home/panels/fleet/FleetMonitor';
import { PanelTitleContext } from '../../features/home/panels/HomePanel';
import { MapLegend } from '../../features/home/panels/legend/MapLegend';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import {
    addPanelToWindow,
    removePanelFromWindow,
    setWindowPanelOrder,
    WorkspaceWindowPanelState,
    WorkspaceWindowState
} from '../../state/slices/workspace-slice';
import CustomPopover from '../layout/CustomPopover';
import { AirportSummaryExternalWrapper } from './AirportSummaryExternalWrapper';
import { getMaxPanelCount, MultiWindowContext } from './context/multi-window-context';
import './ExternalWindow.css';
import { ExternalWindowPortal } from './ExternalWindowPortal';
import { WorkspaceWindowPanelType } from './types';

const DEFAULT_PANEL_ORDER = [
    WorkspaceWindowPanelType.mapLegend,
    WorkspaceWindowPanelType.eventList,
    WorkspaceWindowPanelType.fleetMonitor,
    WorkspaceWindowPanelType.airportMonitor,
    WorkspaceWindowPanelType.flightList,
    WorkspaceWindowPanelType.airportList,
    WorkspaceWindowPanelType.routeAnalyzer,
    WorkspaceWindowPanelType.airportSummary
];

export default function ExternalWindow(props: { window: WorkspaceWindowState }) {
    const dispatch = useAppDispatch();
    const sensors = useSensors(useSensor(PointerSensor));
    const [root, setRoot] = React.useState<HTMLElement>();
    const [wnd, setWindow] = React.useState<WindowProxy>();

    const handleDragEnd = React.useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            dispatch(setWindowPanelOrder({ windowId: props.window.id, panelId: active.id as number, swapWithId: over.id as number }));
        }
    }, []);

    const windows = useAppSelector((x) => x.workspace.windows);
    const availableTypes = useMemo(
        () =>
            DEFAULT_PANEL_ORDER.filter(
                (panelType) =>
                    windows.reduce((p, n) => p + n.panels.reduce((p, n) => (n.type === panelType ? p + 1 : p), 0), 0) <
                    getMaxPanelCount(panelType)
            ),
        [windows]
    );

    return (
        <ExternalWindowPortal
            window={props.window}
            onCreate={(window, root) => {
                setWindow(window);
                setRoot(root);
            }}>
            <MultiWindowContext.Provider value={React.useMemo(() => ({ active: true, root, window: wnd }), [root, wnd])}>
                <div className="d-flex flex-column h-100 w-100">
                    <ContextMenus />

                    <div className="border-bottom d-flex align-items-center justify-content-between ps-1" style={{ fontSize: '0.8rem' }}>
                        <div>
                            <div>Flight Explorer - {props.window.name}</div>
                        </div>
                        <CustomPopover trigger={<Button svgIcon={AddOutline} fillMode="flat" className="button-small" />}>
                            <Menu label="">
                                {availableTypes.map((x) => (
                                    <MenuItem
                                        icon={panelIcons[x]}
                                        key={x}
                                        label={panelNames[x]}
                                        onClick={() => dispatch(addPanelToWindow({ windowId: props.window.id, panel: x }))}
                                    />
                                ))}
                            </Menu>
                        </CustomPopover>
                    </div>

                    <div className="d-flex min-h-0 w-100 h-100 panel-list">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToParentElement]}>
                            <SortableContext items={props.window.panels} strategy={horizontalListSortingStrategy}>
                                {props.window.panels.map((x) => (
                                    <WindowPanelItem key={x.id} panel={x} windowId={props.window.id} />
                                ))}
                            </SortableContext>
                        </DndContext>
                        {props.window.panels.length === 0 && (
                            <div className="flex-grow-1 min-h-0 text-center my-auto secondary-text">No panels open.</div>
                        )}
                    </div>
                </div>
            </MultiWindowContext.Provider>
        </ExternalWindowPortal>
    );
}

function WindowTypeComponent(props: { windowId: number; panel: WorkspaceWindowPanelState }) {
    switch (props.panel.type) {
        case WorkspaceWindowPanelType.mapLegend:
            return <MapLegend />;
        case WorkspaceWindowPanelType.eventList:
            return <EventList />;
        case WorkspaceWindowPanelType.airportMonitor:
            return <AirportMonitor />;
        case WorkspaceWindowPanelType.fleetMonitor:
            return <FleetMonitor />;
        case WorkspaceWindowPanelType.flightList:
            return <FlightList />;
        case WorkspaceWindowPanelType.airportList:
            return <AirportList />;
        case WorkspaceWindowPanelType.routeAnalyzer:
            return <RouteAnalyzer className="w-100" />;
        case WorkspaceWindowPanelType.airportSummary:
            return <AirportSummaryExternalWrapper panel={props.panel} windowId={props.windowId} />;
    }
}

function WindowPanelItem(props: { windowId: number; panel: WorkspaceWindowPanelState }) {
    const dispatch = useAppDispatch();
    const eventListExpanded = useAppSelector((x) => x.settings.eventListExpandedView);
    const [leadingContainer, setLeadingContainer] = useState<HTMLElement | null>(null);
    const [trailingContainer, setTrailingContainer] = useState<HTMLElement | null>(null);

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.panel.id });
    const style = { transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null), transition };

    return (
        <PanelTitleContext.Provider
            value={useMemo(
                () => ({ leadingContentContainer: leadingContainer, trailingContentContainer: trailingContainer }),
                [leadingContainer, trailingContainer]
            )}>
            <div
                ref={setNodeRef}
                style={{ ...style, ...getPanelStyle(props.panel.type, eventListExpanded) }}
                className="h-100 d-flex flex-column bg-color">
                <div className="d-flex align-items-center border-bottom" style={{ fontSize: '0.8rem' }}>
                    <Button svgIcon={DragOutline} fillMode="flat" className="button-small" {...attributes} {...listeners} />
                    <div ref={setLeadingContainer}></div>
                    <div className="flex-grow-1 ps-1">
                        {panelNames[props.panel.type]} {props.panel.airportId && <span> - {props.panel.airportId}</span>}
                        {/* <code>{props.panel.id}</code> */}
                    </div>
                    <div ref={setTrailingContainer}></div>
                    <Button
                        title="Close panel"
                        data-tooltip-bottom
                        svgIcon={CloseOutline}
                        className="button-small"
                        fillMode="flat"
                        onClick={() => dispatch(removePanelFromWindow({ windowId: props.windowId, panelId: props.panel.id }))}
                    />
                </div>
                <div className="flex-grow-1 min-h-0">
                    <WindowTypeComponent panel={props.panel} windowId={props.windowId} />
                </div>
            </div>
        </PanelTitleContext.Provider>
    );
}

const panelNames: Record<WorkspaceWindowPanelType, string> = {
    [WorkspaceWindowPanelType.mapLegend]: 'Map legend',
    [WorkspaceWindowPanelType.eventList]: 'Event list',
    [WorkspaceWindowPanelType.fleetMonitor]: 'Fleet monitor',
    [WorkspaceWindowPanelType.airportMonitor]: 'Airport monitor',
    [WorkspaceWindowPanelType.flightList]: 'Flight list',
    [WorkspaceWindowPanelType.airportList]: 'Airport list',
    [WorkspaceWindowPanelType.routeAnalyzer]: 'Route analyzer',
    [WorkspaceWindowPanelType.airportSummary]: 'Airport summary'
};

const panelIcons: Record<WorkspaceWindowPanelType, SVGIcon> = {
    [WorkspaceWindowPanelType.mapLegend]: AddOutline,
    [WorkspaceWindowPanelType.eventList]: AddOutline,
    [WorkspaceWindowPanelType.fleetMonitor]: AddOutline,
    [WorkspaceWindowPanelType.airportMonitor]: AddOutline,
    [WorkspaceWindowPanelType.flightList]: FlightListOutline,
    [WorkspaceWindowPanelType.airportList]: AirportListOutline,
    [WorkspaceWindowPanelType.routeAnalyzer]: ChartsLineOutline,
    [WorkspaceWindowPanelType.airportSummary]: AirportTowerOutline
};
function getPanelStyle(panel: WorkspaceWindowPanelType, eventListExpanded: boolean): CSSProperties {
    // TODO: This is not ideal. The best way would be to let the panel size itself but that doesn't work with sortable list...
    if (panel === WorkspaceWindowPanelType.eventList) {
        return {
            ...panelStyles[panel],
            width: eventListExpanded ? 400 : 200
        };
    }
    return panelStyles[panel];
}

const panelStyles: Record<WorkspaceWindowPanelType, CSSProperties> = {
    [WorkspaceWindowPanelType.mapLegend]: { width: 150, flexShrink: 0 },
    [WorkspaceWindowPanelType.eventList]: { width: 200, flexShrink: 0 },
    [WorkspaceWindowPanelType.fleetMonitor]: { width: 250, flexShrink: 0 },
    [WorkspaceWindowPanelType.airportMonitor]: { width: 180, flexShrink: 0 },
    [WorkspaceWindowPanelType.flightList]: { width: '100%', minWidth: 0 },
    [WorkspaceWindowPanelType.airportList]: { width: '100%', minWidth: 0 },
    [WorkspaceWindowPanelType.routeAnalyzer]: { width: '100%', minWidth: 0 },
    [WorkspaceWindowPanelType.airportSummary]: { width: '100%', minWidth: 0 }
};
